import { useState, useEffect, useCallback } from "react";
import { useApi } from "./AuthContext";
import {
  UserPlus, Search, Plus, Pencil, Trash2, X, Check, Camera, MapPin,
  Phone, Mail, Home, ImageIcon, Building2, User, FileText, Briefcase,
  Clock, Send, DollarSign, Star
} from "lucide-react";
import { toast } from "sonner";
import BackButton from "./BackButton";
import ClientForm from "./ClientForm";
import { formatUSD } from "../utils/currency";

const EMPTY_FORM = {
  name: "", phone: "", email: "", address: "", city: "", state: "GA",
  zip_code: "", property_type: "residential", notes: "",
  company_name: "", responsible_contact: "", tax_id: "",
  account_manager_id: "", acquired_by_id: "",
  service_ids: [], services_other: "", service_prices: {},
};

const OTHER_SENTINEL = "__other__";

export default function ClientsModule({ readOnly = false, onComposeEmail = null, onCreateTicket = null }) {
  const api = useApi();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [uploading, setUploading] = useState(false);
  const [clientStats, setClientStats] = useState(null);
  const [newNote, setNewNote] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  // Staff directory (for assignment dropdowns)
  const [staff, setStaff] = useState({ managers: [], all_staff: [] });
  // Service catalog (for the "Contracted / Requested Services" checkbox grid)
  const [catalog, setCatalog] = useState([]);
  // Locally tracks the "Others" toggle so the manager can opt-in even when the
  // free-text field is still empty.
  const [otherChecked, setOtherChecked] = useState(false);
  useEffect(() => {
    let cancelled = false;
    api.get("/admin/staff-directory")
      .then(({ data }) => { if (!cancelled) setStaff(data || { managers: [], all_staff: [] }); })
      .catch(() => { /* not fatal — dropdowns just stay empty */ });
    api.get("/admin/service-catalog")
      .then(({ data }) => { if (!cancelled) setCatalog(Array.isArray(data) ? data : []); })
      .catch(() => { /* not fatal — the section just stays empty */ });
    return () => { cancelled = true; };
  }, [api]);

  const fetchClients = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/clients?search=${search}`);
      setClients(data);
    } catch (err) { toast.error("Failed to load clients"); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const fetchClientStats = useCallback(async (clientId) => {
    try {
      const { data } = await api.get(`/admin/clients/${clientId}/stats`);
      setClientStats(data);
    } catch (err) { setClientStats(null); }
  }, [api]);

  useEffect(() => {
    if (selectedClient?.id) fetchClientStats(selectedClient.id);
    else setClientStats(null);
  }, [selectedClient?.id, fetchClientStats]);

  const resetForm = () => { setForm(EMPTY_FORM); setEditingClient(null); setShowForm(false); setOtherChecked(false); };
  // Cancel the edit form WITHOUT losing the selected-client context (so the
  // user stays on the unified single-screen view).
  const cancelEdit = () => { setForm(EMPTY_FORM); setEditingClient(null); setShowForm(false); setOtherChecked(false); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.property_type === "commercial" && !form.responsible_contact?.trim()) {
      toast.error("Responsible Contact is required for commercial clients");
      return;
    }
    try {
      const payload = { ...form };
      if (payload.property_type === "residential") {
        payload.company_name = "";
        payload.responsible_contact = "";
        payload.tax_id = "";
      } else if (payload.property_type === "commercial") {
        // For commercial clients, company_name = name (the establishment name)
        payload.company_name = payload.name;
      }
      // Normalize FK assignments — backend expects null when unselected
      payload.account_manager_id = payload.account_manager_id || null;
      payload.acquired_by_id = payload.acquired_by_id || null;
      // If "Others" is unchecked, clear any leftover free-text
      if (!otherChecked) payload.services_other = "";
      else payload.services_other = (payload.services_other || "").trim();
      payload.service_ids = Array.isArray(payload.service_ids) ? payload.service_ids : [];
      // Only persist prices for services that are still checked
      const checkedSet = new Set(payload.service_ids);
      const cleanedPrices = {};
      Object.entries(payload.service_prices || {}).forEach(([id, val]) => {
        if (checkedSet.has(id)) cleanedPrices[id] = Number(val) || 0;
      });
      payload.service_prices = cleanedPrices;
      // When editing, the "Initial Notes" textarea becomes an "add a note"
      // input — push it via the dedicated /notes endpoint so the history
      // captures created_at + created_by. Strip from the update payload so
      // we don't double-store the same text on the `notes` field.
      const noteToAppend = editingClient && payload.notes ? payload.notes.trim() : "";
      if (editingClient) payload.notes = "";
      if (editingClient) {
        await api.put(`/admin/clients/${editingClient.id}`, payload);
        if (noteToAppend) {
          try { await api.post(`/admin/clients/${editingClient.id}/notes`, { text: noteToAppend }); }
          catch (_) { /* note failure shouldn't block the save */ }
        }
        toast.success("Client updated");
        if (selectedClient?.id === editingClient.id) {
          const { data } = await api.get(`/admin/clients/${editingClient.id}`);
          setSelectedClient(data);
        }
      } else {
        await api.post("/admin/clients", payload);
        toast.success("Client created");
      }
      resetForm();
      fetchClients();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operation failed");
    }
  };

  const handleEdit = (c) => {
    setEditingClient(c);
    setForm({
      name: c.name, phone: c.phone, email: c.email || "",
      address: c.address || "", city: c.city || "", state: c.state || "GA",
      zip_code: c.zip_code || "", property_type: c.property_type || "residential",
      notes: "", company_name: c.company_name || "",
      responsible_contact: c.responsible_contact || "", tax_id: c.tax_id || "",
      account_manager_id: c.account_manager_id || "",
      acquired_by_id: c.acquired_by_id || "",
      service_ids: Array.isArray(c.service_ids) ? c.service_ids : [],
      services_other: c.services_other || "",
      service_prices: (c.service_prices && typeof c.service_prices === "object") ? { ...c.service_prices } : {},
    });
    setOtherChecked(Boolean(c.services_other));
    setShowForm(true);
    // Keep the client selected so the unified single-screen view stays put:
    // the form simply replaces the read-only info card while every other
    // section (notes history, documents, facade, photos) remains visible.
    setSelectedClient(c);
  };

  const toggleServiceId = (id) => {
    setForm(f => {
      const set = new Set(f.service_ids || []);
      const prices = { ...(f.service_prices || {}) };
      if (set.has(id)) {
        set.delete(id);
        // Drop any custom price when the service is unchecked — keeps the doc clean
        delete prices[id];
      } else {
        set.add(id);
        // Seed the editable price with the catalog default the first time it's checked
        const item = catalog.find(c => c.id === id);
        if (item && prices[id] === undefined) prices[id] = Number(item.default_price) || 0;
      }
      return { ...f, service_ids: Array.from(set), service_prices: prices };
    });
  };

  const updateServicePrice = (id, value) => {
    setForm(f => ({
      ...f,
      service_prices: { ...(f.service_prices || {}), [id]: Number(value) || 0 },
    }));
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete client "${c.name}"?`)) return;
    try {
      await api.delete(`/admin/clients/${c.id}`);
      toast.success("Client deleted");
      if (selectedClient?.id === c.id) setSelectedClient(null);
      fetchClients();
    } catch (err) { toast.error("Failed to delete client"); }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !selectedClient) return;
    setAddingNote(true);
    try {
      const { data } = await api.post(`/admin/clients/${selectedClient.id}/notes`, { text: newNote.trim() });
      setSelectedClient(data);
      setNewNote("");
      toast.success("Note added");
    } catch (err) { toast.error("Failed to add note"); }
    finally { setAddingNote(false); }
  };

  // Quick Email Compose: opens internal Webmail with prefill + logs timeline note on the client
  const handleComposeEmail = async (client, opts = {}) => {
    if (!client?.email) { toast.error("Client has no email"); return; }
    if (!onComposeEmail) { toast.error("Email composer unavailable"); return; }
    onComposeEmail({
      to: client.email,
      subject: opts.subject || "",
      body: opts.body || "",
      source: opts.source || "client",
      clientId: client.id,
    });
    // Best-effort: log a note on the client's timeline (silent failure)
    try {
      const noteText = opts.subject
        ? `Email composition started — subject: "${opts.subject}"`
        : "Email composition started";
      const { data } = await api.post(`/admin/clients/${client.id}/notes`, { text: noteText });
      if (selectedClient && selectedClient.id === client.id) setSelectedClient(data);
    } catch (e) { /* non-blocking */ }
  };

  const handlePhotoUpload = async (e) => {
    if (!selectedClient) return;
    const files = e.target.files;
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        await api.post(`/admin/clients/${selectedClient.id}/photos`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
      }
      toast.success(`${files.length} photo(s) uploaded`);
      const { data } = await api.get(`/admin/clients/${selectedClient.id}`);
      setSelectedClient(data);
      fetchClients();
    } catch (err) { toast.error("Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!selectedClient) return;
    try {
      await api.delete(`/admin/clients/${selectedClient.id}/photos/${photoId}`);
      toast.success("Photo removed");
      const { data } = await api.get(`/admin/clients/${selectedClient.id}`);
      setSelectedClient(data);
    } catch (err) { toast.error("Failed to remove photo"); }
  };

  // ---------- Facade photo (single, permanent property ID) ----------
  const [facadeUploading, setFacadeUploading] = useState(false);
  const [facadeUrl, setFacadeUrl] = useState(null);

  const handleFacadeUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedClient) return;
    setFacadeUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await api.post(`/admin/clients/${selectedClient.id}/facade`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Facade photo updated");
      const { data } = await api.get(`/admin/clients/${selectedClient.id}`);
      setSelectedClient(data);
      fetchClients();
    } catch (err) { toast.error("Facade upload failed"); }
    finally { setFacadeUploading(false); e.target.value = ""; }
  };

  const handleDeleteFacade = async () => {
    if (!selectedClient?.facade_photo) return;
    if (!window.confirm("Remove the facade photo? New tickets won't auto-include it.")) return;
    try {
      await api.delete(`/admin/clients/${selectedClient.id}/facade`);
      toast.success("Facade photo removed");
      const { data } = await api.get(`/admin/clients/${selectedClient.id}`);
      setSelectedClient(data);
      setFacadeUrl(null);
    } catch (err) { toast.error("Failed to remove facade photo"); }
  };

  // ---------- Documents (PDF/contracts/proposals/addenda) ----------
  const [docUploading, setDocUploading] = useState(false);
  const [docKind, setDocKind] = useState("contract");

  const handleDocumentUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedClient) return;
    setDocUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("kind", docKind || "other");
      formData.append("label", file.name);
      await api.post(`/admin/clients/${selectedClient.id}/documents`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document uploaded");
      const { data } = await api.get(`/admin/clients/${selectedClient.id}`);
      setSelectedClient(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setDocUploading(false); e.target.value = ""; }
  };

  const handleDeleteDocument = async (docId, name) => {
    if (!window.confirm(`Delete "${name || "document"}"?`)) return;
    try {
      await api.delete(`/admin/clients/${selectedClient.id}/documents/${docId}`);
      toast.success("Document removed");
      const { data } = await api.get(`/admin/clients/${selectedClient.id}`);
      setSelectedClient(data);
    } catch (err) { toast.error("Failed to remove document"); }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const resp = await api.get(`/admin/files/${doc.storage_path}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_filename || "document";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (err) { toast.error("Download failed"); }
  };

  const formatBytes = (b) => {
    const n = Number(b) || 0;
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Resolve the storage path → blob URL whenever the client's facade changes.
  useEffect(() => {
    let cancelled = false;
    const fp = selectedClient?.facade_photo;
    if (!fp?.storage_path) { setFacadeUrl(null); return; }
    api.get(`/admin/files/${fp.storage_path}`, { responseType: "blob" })
      .then(resp => { if (!cancelled) setFacadeUrl(URL.createObjectURL(resp.data)); })
      .catch(() => { /* keep null */ });
    return () => { cancelled = true; };
  }, [api, selectedClient?.facade_photo]);

  const [photoUrls, setPhotoUrls] = useState({});

  useEffect(() => {
    if (!selectedClient?.photos?.length) { setPhotoUrls({}); return; }
    let cancelled = false;
    const loadPhotos = async () => {
      const urls = {};
      for (const photo of selectedClient.photos) {
        if (cancelled) return;
        try {
          const resp = await api.get(`/admin/files/${photo.storage_path}`, { responseType: "blob" });
          urls[photo.id] = URL.createObjectURL(resp.data);
        } catch (err) { /* non-critical */ }
      }
      if (!cancelled) setPhotoUrls(urls);
    };
    loadPhotos();
    return () => { cancelled = true; };
  }, [api, selectedClient?.photos]);

  const isCommercial = (c) => c?.property_type === "commercial";
  const TypeIcon = ({ client, className = "w-4 h-4" }) =>
    isCommercial(client)
      ? <Building2 className={`${className} text-blue-600`} />
      : <Home className={`${className} text-green-600`} />;

  if (loading) return <div className="flex items-center justify-center h-64 text-stone-500">Loading...</div>;

  // ====================== DETAIL VIEW ======================
  if (selectedClient) {
    const notesHistory = selectedClient.notes_history || [];

    return (
      <div data-testid="client-detail" className="space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <BackButton onClick={() => setSelectedClient(null)} testId="back-to-list-btn" label="Back to list" />
          {onCreateTicket && (
            <button
              type="button"
              data-testid="create-new-ticket-btn"
              onClick={() => onCreateTicket(selectedClient.id)}
              className="inline-flex items-center gap-1.5 bg-forest-600 hover:bg-forest-700 text-white px-3.5 py-2 rounded-lg text-sm font-medium shadow-sm transition-colors min-h-[44px]"
            >
              <Plus className="w-4 h-4" /> Create New Ticket
            </button>
          )}
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="client-stats-cards">
          <div className="bg-white rounded-xl border border-stone-200 p-4 flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isCommercial(selectedClient) ? "bg-blue-50" : "bg-green-50"}`}>
              <TypeIcon client={selectedClient} className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs font-medium text-stone-500 uppercase tracking-wider">Type</p>
              <p className="text-sm font-bold text-stone-800 capitalize">{selectedClient.property_type || "Residential"}</p>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-stone-200 p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-forest-50 flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-forest-600" />
            </div>
            <div>
              <p className="text-xs font-medium text-stone-500 uppercase tracking-wider">Total Jobs</p>
              <p className="text-sm font-bold text-stone-800" data-testid="stat-total-jobs">{clientStats?.total_jobs ?? "..."}</p>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-stone-200 p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-xs font-medium text-stone-500 uppercase tracking-wider">Balance Due</p>
              <p className="text-sm font-bold text-amber-700" data-testid="stat-balance-due">{formatUSD(clientStats?.balance_due ?? 0)}</p>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-stone-200 p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center">
              <FileText className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <p className="text-xs font-medium text-stone-500 uppercase tracking-wider">Total Invoiced</p>
              <p className="text-sm font-bold text-teal-700" data-testid="stat-total-invoiced">{formatUSD(clientStats?.total_invoiced ?? 0)}</p>
            </div>
          </div>
        </div>

        {/* Client Info Card — hidden while the inline Edit Form is open so
            the form replaces it on the same single screen. */}
        {!showForm && (
        <div className="bg-white rounded-xl border border-stone-200 p-6 shadow-sm">
          <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isCommercial(selectedClient) ? "bg-blue-50 border border-blue-200" : "bg-green-50 border border-green-200"}`}>
                <TypeIcon client={selectedClient} className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-stone-800" data-testid="client-detail-name">{selectedClient.name}</h2>
                {isCommercial(selectedClient) && selectedClient.responsible_contact && (
                  <p className="text-sm text-stone-500 flex items-center gap-1">
                    <User className="w-3.5 h-3.5" /> Attn: {selectedClient.responsible_contact}
                  </p>
                )}
                {isCommercial(selectedClient) && selectedClient.tax_id && (
                  <p className="text-xs text-stone-400 mt-0.5">Tax ID / EIN: {selectedClient.tax_id}</p>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              {!readOnly && <button onClick={() => handleEdit(selectedClient)} data-testid="edit-client-btn" className="flex items-center gap-1.5 bg-forest-600 hover:bg-forest-700 text-white px-3 py-1.5 rounded-lg text-sm transition-colors"><Pencil className="w-3.5 h-3.5" /> Edit</button>}
              {!readOnly && <button onClick={() => handleDelete(selectedClient)} data-testid="delete-client-detail-btn" className="flex items-center gap-1.5 bg-red-50 hover:bg-red-100 text-red-600 px-3 py-1.5 rounded-lg text-sm transition-colors"><Trash2 className="w-3.5 h-3.5" /> Delete</button>}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2 text-stone-600"><Phone className="w-4 h-4 text-stone-400" />{selectedClient.phone || "—"}</div>
            {selectedClient.email && (
              <div className="flex items-center gap-2 text-stone-600">
                <Mail className="w-4 h-4 text-stone-400" />
                <button
                  type="button"
                  data-testid={`compose-email-profile-${selectedClient.id}`}
                  onClick={() => handleComposeEmail(selectedClient, { source: "client_profile" })}
                  className="text-forest-700 hover:text-forest-800 hover:underline underline-offset-2 transition-colors"
                  title="Compose email in Webmail"
                >
                  {selectedClient.email}
                </button>
              </div>
            )}
            <div className="flex items-center gap-2 text-stone-600"><MapPin className="w-4 h-4 text-stone-400" />{[selectedClient.address, selectedClient.city, selectedClient.state, selectedClient.zip_code].filter(Boolean).join(", ") || "—"}</div>
          </div>
          {/* Internal Assignment readout */}
          {(selectedClient.account_manager_id || selectedClient.acquired_by_id) && (
            <div className="mt-4 pt-4 border-t border-stone-100 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm" data-testid="client-assignment-readout">
              {(() => {
                const am = staff.managers.find(s => s.id === selectedClient.account_manager_id)
                  || staff.all_staff.find(s => s.id === selectedClient.account_manager_id);
                return selectedClient.account_manager_id ? (
                  <div className="flex items-center gap-2 text-stone-600">
                    <Briefcase className="w-4 h-4 text-stone-400" />
                    <span className="text-xs text-stone-400 mr-1">Manager:</span>
                    <span className="font-medium text-stone-700">{am?.name || "—"}</span>
                  </div>
                ) : null;
              })()}
              {(() => {
                const ab = staff.all_staff.find(s => s.id === selectedClient.acquired_by_id);
                return selectedClient.acquired_by_id ? (
                  <div className="flex items-center gap-2 text-stone-600">
                    <UserPlus className="w-4 h-4 text-stone-400" />
                    <span className="text-xs text-stone-400 mr-1">Acquired by:</span>
                    <span className="font-medium text-stone-700">{ab?.name || "—"}</span>
                  </div>
                ) : null;
              })()}
            </div>
          )}
        </div>
        )}

        {/* Edit Form (inline, single-screen UX) — appears in place of the
            read-only info card above when the user clicks Edit. */}
        {!readOnly && showForm && editingClient && editingClient.id === selectedClient.id && (
          <ClientForm
            editingClient={editingClient}
            form={form}
            setForm={setForm}
            onSubmit={handleSubmit}
            onCancel={cancelEdit}
            onCreateTicket={onCreateTicket}
            otherChecked={otherChecked}
            setOtherChecked={setOtherChecked}
            catalog={catalog}
            staff={staff}
            toggleServiceId={toggleServiceId}
            updateServicePrice={updateServicePrice}
          />
        )}

        {/* Notes History */}
        <div className="bg-white rounded-xl border border-stone-200 p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-5 h-5 text-forest-600" />
            <h3 className="font-semibold text-stone-800">Notes & History</h3>
            <span className="text-xs text-stone-400">({notesHistory.length})</span>
          </div>

          {/* Add Note */}
          {!readOnly && (
            <div className="flex gap-2 mb-4" data-testid="add-note-section">
              <input
                data-testid="new-note-input"
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleAddNote(); } }}
                placeholder="Add a note..."
                className="flex-1 px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-forest-500 focus:border-forest-500"
              />
              <button
                onClick={handleAddNote}
                disabled={!newNote.trim() || addingNote}
                data-testid="add-note-btn"
                className="flex items-center gap-1.5 bg-forest-600 hover:bg-forest-700 disabled:bg-stone-300 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                <Send className="w-3.5 h-3.5" /> Add
              </button>
            </div>
          )}

          {/* Notes List */}
          {notesHistory.length > 0 ? (
            <div className="space-y-3 max-h-80 overflow-y-auto" data-testid="notes-history-list">
              {[...notesHistory].reverse().map((note) => (
                <div key={note.id} className="bg-stone-50 rounded-lg px-4 py-3 border border-stone-100" data-testid={`note-${note.id}`}>
                  <p className="text-sm text-stone-700">{note.text}</p>
                  <div className="flex items-center gap-3 mt-2 text-xs text-stone-400">
                    <span className="flex items-center gap-1"><User className="w-3 h-3" />{note.created_by}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(note.created_at).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true })}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-stone-400 text-center py-4">No notes yet.</p>
          )}

          {/* Legacy notes fallback */}
          {selectedClient.notes && !notesHistory.length && (
            <div className="mt-3 p-3 bg-amber-50 rounded-lg text-sm text-stone-600 border border-amber-100">
              <p className="text-xs font-medium text-amber-600 mb-1">Legacy Note</p>
              {selectedClient.notes}
            </div>
          )}
        </div>

        {/* Documents (PDFs, contracts, proposals, addenda — auto-listed with date/uploader) */}
        <div className="bg-white rounded-xl border border-stone-200 p-6 shadow-sm" data-testid="client-documents-card">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-teal-600" />
              <h3 className="font-semibold text-stone-800">Documents</h3>
              <span className="text-[11px] text-stone-400 hidden sm:inline">
                — proposals, contracts, addenda · PDFs and other files
              </span>
            </div>
            {!readOnly && (
              <div className="flex items-center gap-2 flex-wrap">
                <select
                  value={docKind}
                  onChange={(e) => setDocKind(e.target.value)}
                  data-testid="document-kind-select"
                  className="px-2 py-1.5 border border-stone-300 rounded-lg text-xs bg-white focus:ring-2 focus:ring-teal-500"
                >
                  <option value="contract">Contract</option>
                  <option value="proposal">Proposal</option>
                  <option value="addendum">Addendum</option>
                  <option value="receipt">Receipt</option>
                  <option value="other">Other</option>
                </select>
                <label className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-3 py-1.5 rounded-lg text-sm cursor-pointer transition-colors">
                  <Plus className="w-4 h-4" /> Upload
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.heic,.txt"
                    onChange={handleDocumentUpload}
                    className="hidden"
                    data-testid="document-upload-input"
                  />
                </label>
              </div>
            )}
          </div>
          {docUploading && <div className="text-sm text-stone-500 mb-3">Uploading...</div>}
          {Array.isArray(selectedClient.documents) && selectedClient.documents.length > 0 ? (
            <ul className="divide-y divide-stone-100 border border-stone-200 rounded-lg overflow-hidden" data-testid="documents-list">
              {[...selectedClient.documents]
                .sort((a, b) => (b.uploaded_at || "").localeCompare(a.uploaded_at || ""))
                .map((d) => (
                <li key={d.id} className="px-3 py-2.5 flex items-center gap-3 hover:bg-stone-50 transition-colors" data-testid={`document-${d.id}`}>
                  <div className="w-9 h-9 rounded-lg bg-teal-50 flex items-center justify-center shrink-0">
                    <FileText className="w-4.5 h-4.5 text-teal-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium text-stone-800 truncate" title={d.original_filename}>{d.original_filename}</p>
                      <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-stone-100 text-stone-600">
                        {(d.kind || "other")}
                      </span>
                    </div>
                    <p className="text-[11px] text-stone-400 mt-0.5">
                      {formatBytes(d.size)} · {d.uploaded_by || "—"} · {d.uploaded_at ? new Date(d.uploaded_at).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : "—"}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDownloadDocument(d)}
                    data-testid={`document-download-${d.id}`}
                    className="p-1.5 rounded-md hover:bg-teal-50 text-stone-500 hover:text-teal-700 transition-colors"
                    title="Download"
                  >
                    <Send className="w-4 h-4 rotate-90" />
                  </button>
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => handleDeleteDocument(d.id, d.original_filename)}
                      data-testid={`document-delete-${d.id}`}
                      className="p-1.5 rounded-md hover:bg-red-50 text-stone-500 hover:text-red-600 transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-center py-8 text-stone-400 border-2 border-dashed border-stone-200 rounded-lg">
              <FileText className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No documents yet. Pick a type above and upload contracts, proposals or PDFs.</p>
            </div>
          )}
        </div>

        {/* Facade Photo (single, permanent property identifier — auto-attached to every new ticket) */}
        <div className="bg-white rounded-xl border border-stone-200 p-6 shadow-sm" data-testid="client-facade-card">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Star className="w-5 h-5 text-amber-500" />
              <h3 className="font-semibold text-stone-800">Property Facade Photo</h3>
              <span className="text-[11px] text-stone-400 hidden sm:inline">— auto-attached to every new ticket</span>
            </div>
            {!readOnly && (
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 rounded-lg text-sm cursor-pointer transition-colors">
                  <Plus className="w-4 h-4" /> {selectedClient.facade_photo ? "Replace" : "Upload"}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFacadeUpload}
                    className="hidden"
                    data-testid="facade-upload-input"
                  />
                </label>
                {selectedClient.facade_photo && (
                  <button
                    type="button"
                    onClick={handleDeleteFacade}
                    data-testid="facade-delete-btn"
                    className="p-1.5 rounded-md hover:bg-red-50 text-stone-500 hover:text-red-600"
                    title="Remove facade photo"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}
          </div>
          {facadeUploading && <div className="text-sm text-stone-500 mb-3">Uploading...</div>}
          {selectedClient.facade_photo ? (
            <div className="flex flex-col sm:flex-row gap-4 items-start">
              <div
                data-testid="facade-photo-preview"
                className="relative rounded-xl overflow-hidden border-2 border-amber-200 bg-stone-100 w-full sm:w-64 aspect-[4/3] shadow-sm shrink-0"
              >
                {facadeUrl ? (
                  <img src={facadeUrl} alt="Property facade" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><ImageIcon className="w-8 h-8 text-stone-300" /></div>
                )}
                <div className="absolute top-2 left-2 bg-amber-500 text-white text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full shadow-sm flex items-center gap-1">
                  <Star className="w-3 h-3" /> Facade
                </div>
              </div>
              <div className="flex-1 text-sm text-stone-500 leading-relaxed">
                <p className="text-stone-700 font-medium mb-1">Permanent property identifier</p>
                <p>This photo follows the client. Every new Service Order and Field App ticket created for this client will automatically show it as the master facade — even if the field crew hits an unfamiliar address or GPS confusion.</p>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-stone-400 border-2 border-dashed border-stone-200 rounded-lg">
              <Star className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No facade photo yet — upload one above. It will appear on every new ticket for this client.</p>
            </div>
          )}
        </div>

        {/* Photos */}
        <div className="bg-white rounded-xl border border-stone-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-forest-600" />
              <h3 className="font-semibold text-stone-800">Property Photos</h3>
              <span className="text-xs text-stone-400">({selectedClient.photos?.length || 0})</span>
            </div>
            {!readOnly && (
              <label className="flex items-center gap-2 bg-forest-600 hover:bg-forest-700 text-white px-3 py-1.5 rounded-lg text-sm cursor-pointer transition-colors">
                <Plus className="w-4 h-4" /> Upload
                <input type="file" accept="image/*" multiple onChange={handlePhotoUpload} className="hidden" data-testid="photo-upload-input" />
              </label>
            )}
          </div>
          {uploading && <div className="text-sm text-stone-500 mb-3">Uploading...</div>}
          {selectedClient.photos?.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {selectedClient.photos.map((photo) => (
                <div key={photo.id} className="relative group rounded-lg overflow-hidden bg-stone-100 aspect-square" data-testid={`photo-${photo.id}`}>
                  {photoUrls[photo.id] ? (
                    <img src={photoUrls[photo.id]} alt={photo.original_filename} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><ImageIcon className="w-8 h-8 text-stone-300" /></div>
                  )}
                  {!readOnly && (
                    <button onClick={() => handleDeletePhoto(photo.id)} className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                      <X className="w-3 h-3" />
                    </button>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs px-2 py-1 truncate">
                    {photo.original_filename}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-stone-400">
              <Camera className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No photos yet. Upload property photos above.</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ====================== LIST VIEW ======================
  return (
    <div data-testid="clients-module" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-forest-50 rounded-lg flex items-center justify-center">
            <UserPlus className="w-5 h-5 text-forest-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-stone-800">Client Registration</h2>
            <p className="text-sm text-stone-500">{clients.length} client{clients.length !== 1 ? "s" : ""}</p>
          </div>
        </div>
        {!readOnly && (
          <button data-testid="add-client-btn" onClick={() => { resetForm(); setShowForm(true); }}
            className="flex items-center gap-2 bg-forest-600 hover:bg-forest-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
            <Plus className="w-4 h-4" /> New Client
          </button>
        )}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
        <input
          data-testid="client-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, company, phone, email, or city..."
          className="w-full pl-10 pr-4 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-forest-500 focus:border-forest-500"
        />
      </div>

      {/* Edit Form — single screen UX: shown above the table for "New Client",
          or replaces the read-only Client Info Card inside the Detail view
          when editing an existing one. */}
      {!readOnly && showForm && (
        <ClientForm
          editingClient={editingClient}
          form={form}
          setForm={setForm}
          onSubmit={handleSubmit}
          onCancel={cancelEdit}
          onCreateTicket={onCreateTicket}
          otherChecked={otherChecked}
          setOtherChecked={setOtherChecked}
          catalog={catalog}
          staff={staff}
          toggleServiceId={toggleServiceId}
          updateServicePrice={updateServicePrice}
        />
      )}

      {/* Clients Table   </div>
      )}

      {/* Clients Table — only when not editing/creating, so the form gets the
          full screen and the manager isn't distracted by the list. */}
      {!showForm && (
      <div className="bg-white rounded-xl border border-stone-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="clients-table">
            <thead>
              <tr className="border-b border-stone-200 bg-stone-50">
                <th className="text-left px-4 py-3 font-medium text-stone-600">Name</th>
                <th className="text-left px-4 py-3 font-medium text-stone-600 hidden md:table-cell">Contact</th>
                <th className="text-left px-4 py-3 font-medium text-stone-600 hidden md:table-cell">City</th>
                <th className="text-left px-4 py-3 font-medium text-stone-600 hidden lg:table-cell">Type</th>
                <th className="text-left px-4 py-3 font-medium text-stone-600 hidden lg:table-cell">Photos</th>
                <th className="text-right px-4 py-3 font-medium text-stone-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {clients.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-stone-400">No clients found</td></tr>
              ) : clients.map((c) => (
                <tr key={c.id} className="border-b border-stone-100 hover:bg-stone-50 cursor-pointer transition-colors" onClick={() => setSelectedClient(c)} data-testid={`client-row-${c.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isCommercial(c) ? "bg-blue-50" : "bg-green-50"}`}>
                        <TypeIcon client={c} className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="font-medium text-stone-800">{c.name}</p>
                        {isCommercial(c) && c.responsible_contact && (
                          <p className="text-xs text-stone-400">Attn: {c.responsible_contact}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-stone-600 hidden md:table-cell">
                    <div className="space-y-0.5">
                      <p className="text-xs">{c.phone}</p>
                      {c.email && (
                        <button
                          type="button"
                          data-testid={`compose-email-list-${c.id}`}
                          onClick={(e) => { e.stopPropagation(); handleComposeEmail(c, { source: "client_list" }); }}
                          className="text-xs text-forest-700 hover:text-forest-800 hover:underline underline-offset-2 transition-colors text-left"
                          title="Compose email in Webmail"
                        >
                          {c.email}
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-stone-600 hidden md:table-cell text-xs">{c.city}{c.state ? `, ${c.state}` : ""}</td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${isCommercial(c) ? "bg-blue-50 text-blue-700" : "bg-green-50 text-green-700"}`}>
                      {c.property_type || "residential"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-stone-600 hidden lg:table-cell text-xs">{c.photos?.length || 0}</td>
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      {!readOnly && <button onClick={() => handleEdit(c)} data-testid={`edit-client-${c.id}`} className="p-1.5 rounded-md hover:bg-stone-100 text-stone-500 hover:text-forest-600 transition-colors"><Pencil className="w-4 h-4" /></button>}
                      {!readOnly && <button onClick={() => handleDelete(c)} data-testid={`delete-client-${c.id}`} className="p-1.5 rounded-md hover:bg-red-50 text-stone-500 hover:text-red-600 transition-colors"><Trash2 className="w-4 h-4" /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      )}
    </div>
  );
}
